import { merge } from "lodash"
import { iJSONObject } from "../../common/types"
import { getFlagEnabled } from "../../getFlagValue"

const UI_FIELD_NAMES = new Set([
    "aggFunc",
    "colDefs",
    "componentType",
    "dataType",
    "default_value",
    "editable", // we have a limited use case for this in colDefs in field forms
    "field",
    "groupBy",
    "groupIncludeTotalFooter",
    "headerName",
    "hidden",
    "label",
    "logic:listener",
    "mobile_layout", // can be removed if/when we remove classic schema migration tests
    "order",
    "primary",
    "related-date-path",
    "secondary",
    "tableSettings",
    "tertiary",
    "uniqueBy",
    "use-project-time",
    "valueGetter",
])

/**
 *
 * @param dataSchema JSON schema holding data types for form
 * @param uiSchema JSON schema holding information for UI display - like order and widgets/components
 * @param permissionSchema schema holding permission info
 * @returns JSON Schema combining the dataSchema with the uiSchema
 */
export const getCombinedSchema = (
    dataSchema: iJSONObject,
    uiSchema: iJSONObject,
    permissionSchema: iJSONObject
): iJSONObject => {
    // make a copy of the dataSchema and pass that into the combiner method
    let combinedSchema = Object.assign({}, dataSchema)
    // this will update the copied schema with the useful uiSchema information
    addUISchemaToDataSchema(combinedSchema, uiSchema)
    // add in the permissions as well - they are the same shape so are more easily merged
    if (permissionSchema) {
        combinedSchema = merge(combinedSchema, permissionSchema)
    }
    if (getFlagEnabled("WA-9079-wage-rates")) {
        propagatePermissions(combinedSchema)
    }
    return combinedSchema
}

/**
 * Recursively propagate editable/viewable permissions from parent nodes down to all
 * descendants. A parent with editable: false or viewable: false locks all children
 * regardless of their own values — permissions only restrict, never grant.
 *
 * @param schema - The combined schema node to propagate permissions through
 * @param parentEditable - The inherited editable permission from ancestors
 * @param parentViewable - The inherited viewable permission from ancestors
 */
export const propagatePermissions = (
    schema: Record<string, any>,
    parentEditable?: boolean,
    parentViewable?: boolean
): void => {
    if (parentEditable === false) {
        schema.editable = false
    }
    if (parentViewable === false) {
        schema.viewable = false
    }

    const editable = schema.editable
    const viewable = schema.viewable

    if (schema.properties) {
        for (const key of Object.keys(schema.properties)) {
            propagatePermissions(schema.properties[key], editable, viewable)
        }
    }
    if (schema.items) {
        propagatePermissions(schema.items, editable, viewable)
    }
}

/**
 *
 * @param combinedSchema JSON schema that will ultimately hold both data and ui information
 * @param uiSchema JSON schema holding information for UI display - like order and widgets/components
 * @returns Fields are added to the combinedSchema passed in
 */
export const addUISchemaToDataSchema = (
    combinedSchema: Record<string, any>,
    uiSchema: Record<string, any>
): void => {
    for (const key in uiSchema) {
        if (key === "items" && "items" in combinedSchema) {
            const uiSchemaItem = uiSchema["items"]
            addUISchemaToDataSchema(combinedSchema["items"], uiSchemaItem)
        } else if ("properties" in combinedSchema && !key.startsWith("ui:") && !UI_FIELD_NAMES.has(key)) {
            for (const field in combinedSchema["properties"]) {
                if ("properties" in combinedSchema && field in combinedSchema["properties"]) {
                    addUISchemaToDataSchema(combinedSchema["properties"][field], uiSchema[field])
                }
            }
        } else {
            combinedSchema[key] = uiSchema[key]
        }
    }
}
